/**
 * NPEAllocationAddDialog — single Dialog port của Access form
 * `frmPurchaseQryNPEAllocation_Add` (Caption "NPEs - Allocation").
 *
 * Layout 3 sections (Access):
 *   1. NPEs Fixation (Input) — fixation_no Autocomplete + DLookup fields
 *      (fixation_date, supplier, contract_no, p_contract_no, price,
 *      quantity_kg, amount_after_fixed, note).
 *   2. Allocated Information — allocated_amount, to_be_allocate,
 *      advanced_date (editable), must_allocate, **subForm (Alc)** với cột
 *      `allocate_value` + `allocate_quantity` editable.
 *   3. NPEs Fixation (Finally) — allocated_quantity, to_be_allocate_quant,
 *      btnRefresh, subForm2 (AlcFinal) read-only preview.
 *
 * Buttons: Save (commit session), Cancel (delete session), Refresh
 * (re-fetch session state).
 *
 * Backend flow:
 *   - User chọn fixation_no → POST /sessions/create { fixation_id } → BE seed
 *     lines từ `v_npe_allocation_alc` (Access choose_fixation_no logic).
 *   - User chỉnh qty/value/interest mỗi row → debounced 500ms PUT /lines.
 *   - Save → POST /commit; Cancel → DELETE /sessions.
 */
import { type HTMLAttributes, type Key, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Divider, Grid, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import InboxIcon from '@mui/icons-material/Inbox'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'
import {
  compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx,
  dialogTitleTextSx, numSx, roSx, sectionHeaderSx,
} from './dialogStyle'
import {
  useCancelAllocationSessionMutation, useCommitAllocationSessionMutation,
  useCreateAllocationSessionMutation, useGetAllocationSessionQuery,
  useUpdateSessionLinesMutation,
} from '@/store/api/purchaseContractApi'
import { useGetNPEFixationQuery, useListNPEFixationsQuery } from '@/store/api/purchaseContractApi'
import type {
  NPEAllocationCommitIn, NPEAllocationLineIn, NPEFixationOut,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const fmtDate = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}
const fmtNum = (v: unknown, digits = 0): string => {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  return digits > 0
    ? n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface LineEditState {
  paydata_p_id: number
  allocate_quantity: number | ''
  allocate_value: number | ''
  allocate_interest: number | ''
}

// Cell input editable — vàng nhạt + border-left primary để user nhận biết "action zone".
const editCellSx = {
  p: 0.5,
  bgcolor: '#fff8e1',
  borderLeft: 3,
  borderColor: 'warning.main',
  '&:hover': { bgcolor: '#fff3c4' },
} as const

const editHeadSx = {
  fontWeight: 800,
  whiteSpace: 'nowrap',
  bgcolor: '#fde68a',
  color: 'warning.dark',
  borderLeft: 3,
  borderColor: 'warning.main',
  letterSpacing: 0.2,
} as const

// Bảng NPEs Fixation (Input)/(Finally) — font 0.8125rem cho khớp Allocated
// Information; table-layout auto + whiteSpace nowrap mọi cell để cột co
// đúng theo độ rộng text. Zebra + hover từ TLC pattern.
// Row height fixed 32px để Input (có TextField) và Finally (text only) cùng
// cao 1 row → 2 bảng cùng tổng height khi cùng N rows.
const compactTableSx = {
  '& .MuiTable-root': { width: 'auto', tableLayout: 'auto' },
  '& .MuiTableCell-root': {
    fontSize: '0.8125rem',
    py: 0,
    px: 1,
    lineHeight: 1.35,
    whiteSpace: 'nowrap',
    height: 32,
  },
  '& tbody .MuiTableRow-root': {
    height: 32,
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    bgcolor: 'grey.100',
    color: 'text.primary',
    height: 36,
  },
  '& tbody .MuiTableRow-root:nth-of-type(even) > .MuiTableCell-root': {
    bgcolor: 'rgba(0, 0, 0, 0.018)',
  },
  '& tbody .MuiTableRow-root:hover > .MuiTableCell-root': {
    bgcolor: 'rgba(25, 118, 210, 0.06)',
  },
} as const

// Sticky cột đầu (Advanced ID) — luôn visible khi scroll ngang để giữ context row.
const stickyFirstColSx = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderRight: 1,
  borderColor: 'divider',
} as const
const stickyFirstHeadSx = {
  fontWeight: 700,
  whiteSpace: 'nowrap' as const,
  position: 'sticky' as const,
  left: 0,
  zIndex: 4,
  bgcolor: 'grey.100',
  borderRight: 1,
  borderColor: 'divider',
}

const totalCellSx = {
  fontWeight: 800,
  fontSize: '0.8125rem',
  bgcolor: '#e3f2fd',
  color: 'primary.dark',
  position: 'sticky' as const,
  bottom: 0,
  zIndex: 1,
  whiteSpace: 'nowrap' as const,
  borderTop: 2,
  borderColor: 'primary.light',
}
const totalStickyFirstSx = {
  ...totalCellSx,
  left: 0,
  zIndex: 3,
}

/**
 * AllocateNumInput — TextField wrapped với format-on-blur thousand separator.
 * Focused: raw number để user edit dễ. Blurred: `1,234,567` cho readability.
 */
function AllocateNumInput({
  value, onChange,
}: { value: number | ''; onChange: (raw: string) => void }) {
  const [focused, setFocused] = useState(false)
  const display = value === '' || value == null
    ? ''
    : focused ? String(value) : Number(value).toLocaleString('en-US')
  return (
    <TextField
      size="small"
      type="text"
      variant="standard"
      fullWidth
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(e.target.value.replace(/[,\s]/g, ''))}
      inputProps={{
        style: { textAlign: 'right', fontSize: '0.8125rem', padding: 2, fontWeight: 600 },
        inputMode: 'decimal',
      }}
    />
  )
}

/**
 * EmptyAllocateState — render OUTSIDE `<Table>` để center theo container Box
 * (width đồng nhất cho cả 2 bảng), KHÔNG bị lệch theo total table width
 * (Input 21 cols vs Finally 18 cols).
 */
function EmptyAllocateState({
  hasFixation, loading,
}: { hasFixation: boolean; loading: boolean }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 4, minHeight: 140 }}>
      {loading ? (
        <>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">Loading advances…</Typography>
        </>
      ) : (
        <>
          <InboxIcon sx={{ fontSize: 36, color: 'action.disabled' }} />
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {hasFixation ? 'No Advanced Value to Allocate' : 'No Fixation selected'}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {hasFixation ? 'This fixation has nothing left to allocate.' : '↑ Pick a Fixation No above to load advances'}
          </Typography>
        </>
      )}
    </Stack>
  )
}

interface NPEAllocationAddDialogProps {
  open: boolean
  onClose: () => void
  onCommitted: () => void
  /** Khi mở từ "Save & Allocate" (NPEFixationFormDialog): fixation đã tạo và
   *  session đã được seed bởi BE /create-and-allocate — bám Access
   *  `btnSaveAllocate_Click` (set fixation_no + call choose_fixation_no). */
  initialFixationId?: number
  initialSessionUid?: string
}

export function NPEAllocationAddDialog({
  open, onClose, onCommitted, initialFixationId, initialSessionUid,
}: NPEAllocationAddDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const [selectedFix, setSelectedFix] = useState<NPEFixationOut | null>(null)
  const [sessionUid, setSessionUid] = useState<string>('')
  const [lineInputs, setLineInputs] = useState<Record<number, LineEditState>>({})
  const [note, setNote] = useState<string>('')
  const [advancedDate, setAdvancedDate] = useState<string>(new Date().toISOString().slice(0, 10))

  // Reset all state on close.
  useEffect(() => {
    if (open) return
    setSelectedFix(null)
    setSessionUid('')
    setLineInputs({})
    setNote('')
    setAdvancedDate(new Date().toISOString().slice(0, 10))
  }, [open])

  // `bypass_date_filter=true` để combo Fixation No hiện mọi fixation chưa
  // allocate hết, bất kể Purchase page-level date filter (delivery_time prefs).
  // Trước đây dùng list filtered → fixation ngoài date range vắng mặt khỏi combo,
  // user phải nới range filter ở page mới allocate được. Workflow allocation
  // logic không phụ thuộc date range của browse list.
  const { data: fixationsRes } = useListNPEFixationsQuery(
    { page: 1, page_size: 1_000_000, bypass_date_filter: true },
    { skip: !open },
  )
  // BE list JOIN v_cs_fixation_p × consignment_p → 1 fixation có nhiều CP →
  // duplicate cs_fixation_id rows. Dedupe để combo không lỗi React duplicate key.
  const fixationOptions: NPEFixationOut[] = useMemo(() => {
    const items = fixationsRes?.items ?? []
    const filtered = items.filter((f) => (f.to_be_allocate ?? 0) !== 0 || (f.allocated_amount ?? 0) === 0)
    const seen = new Set<number>()
    const out: NPEFixationOut[] = []
    for (const f of filtered) {
      if (seen.has(f.cs_fixation_id)) continue
      seen.add(f.cs_fixation_id)
      out.push(f)
    }
    return out
  }, [fixationsRes])

  const [createSession, { isLoading: creating }] = useCreateAllocationSessionMutation()
  const [updateLines] = useUpdateSessionLinesMutation()
  const [commitSession, { isLoading: committing }] = useCommitAllocationSessionMutation()
  const [cancelSession, { isLoading: cancelling }] = useCancelAllocationSessionMutation()

  const { data: sessionRes, isFetching: fetchingState, refetch } =
    useGetAllocationSessionQuery(sessionUid, { skip: !sessionUid, refetchOnMountOrArgChange: true })
  const session = sessionRes?.data

  // Seed lineInputs khi session.lines thay đổi (init từ BE proportional pre-fill).
  const seededRef = useRef<string>('')
  useEffect(() => {
    if (!session) return
    const sig = session.lines.map((l) => `${l.paydata_p_id}:${l.allocate_quantity ?? ''}:${l.allocate_value ?? ''}`).join('|')
    if (seededRef.current === sig) return
    seededRef.current = sig
    const next: Record<number, LineEditState> = {}
    for (const ln of session.lines) {
      next[ln.paydata_p_id] = {
        paydata_p_id: ln.paydata_p_id,
        allocate_quantity: ln.allocate_quantity == null ? '' : Number(ln.allocate_quantity),
        allocate_value: ln.allocate_value == null ? '' : Number(ln.allocate_value),
        allocate_interest: ln.allocate_interest == null ? '' : Number(ln.allocate_interest),
      }
    }
    setLineInputs(next)
    if (session.note != null) setNote(session.note)
    if (session.advanced_date) setAdvancedDate(String(session.advanced_date).slice(0, 10))
  }, [session])

  // Seed selectedFix + sessionUid khi mở từ "Save & Allocate"
  // (BE `/create-and-allocate` đã tạo session + seed lines → bypass auto-create).
  // Bám Access `btnSaveAllocate_Click`: gán fixation_no rồi gọi choose_fixation_no.
  //
  // 2 effect tách rời để dialog hiển thị ngay không phải chờ network:
  //   1. sessionUid set ngay → session GET trigger, read-only fields populate.
  //   2. selectedFix: ưu tiên list cache (instant), fallback get-by-id query.
  const { data: initialFixData } = useGetNPEFixationQuery(initialFixationId ?? 0, {
    skip: !initialFixationId || !open,
  })
  const seededSessionRef = useRef<string>('')
  useEffect(() => {
    if (!open) {
      seededSessionRef.current = ''
      return
    }
    if (!initialSessionUid) return
    if (seededSessionRef.current === initialSessionUid) return
    seededSessionRef.current = initialSessionUid
    setSessionUid(initialSessionUid)
  }, [open, initialSessionUid])
  const seededFixRef = useRef<number>(0)
  useEffect(() => {
    if (!open) {
      seededFixRef.current = 0
      return
    }
    if (!initialFixationId) return
    if (seededFixRef.current === initialFixationId) return
    const fromList = fixationOptions.find((f) => f.cs_fixation_id === initialFixationId)
    const fix = fromList ?? initialFixData?.data
    if (!fix) return
    seededFixRef.current = initialFixationId
    setSelectedFix(fix)
  }, [open, initialFixationId, fixationOptions, initialFixData])

  // Trigger create session khi user chọn fixation_no.
  // `useSnackbar()` return object mới mỗi render → nếu để `snack` trong deps
  // thì effect fire mỗi render trước khi sessionUid được set → POST loop
  // ("Maximum update depth exceeded"). Dùng `creatingRef` để guard duplicate
  // POST + chỉ phụ thuộc primitive state.
  const creatingRef = useRef(false)
  useEffect(() => {
    if (!open || !selectedFix || sessionUid || creatingRef.current) return
    creatingRef.current = true
    void (async () => {
      try {
        const res = await createSession({ fixation_id: selectedFix.cs_fixation_id }).unwrap()
        const uid = res.data?.session_uid
        if (!uid) throw new Error('Missing session_uid')
        setSessionUid(uid)
      } catch (err) {
        snack.error(getErrorMessage(err, 'Could not create session'))
        setSelectedFix(null)
      } finally {
        creatingRef.current = false
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedFix, sessionUid])

  // Debounced auto-save khi user chỉnh qty/value/interest.
  const saveTimerRef = useRef<number | null>(null)
  const scheduleSave = useCallback(
    (next: Record<number, LineEditState>, noteVal: string, dateVal: string) => {
      if (!sessionUid) return
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current)
      saveTimerRef.current = window.setTimeout(() => {
        const lines: NPEAllocationLineIn[] = Object.values(next).map((l) => ({
          paydata_p_id: l.paydata_p_id,
          allocate_quantity: l.allocate_quantity === '' ? null : Number(l.allocate_quantity),
          allocate_value: l.allocate_value === '' ? null : Number(l.allocate_value),
          allocate_interest: l.allocate_interest === '' ? null : Number(l.allocate_interest),
        }))
        void updateLines({ sessionUid, lines, note: noteVal, advancedDate: dateVal })
          .unwrap()
          .catch((err) => snack.error(getErrorMessage(err, 'Auto-save lines failed')))
      }, 500)
    },
    [sessionUid, updateLines, snack],
  )
  useEffect(() => () => { if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current) }, [])

  const handleLineChange = (
    paydataId: number,
    field: 'allocate_quantity' | 'allocate_value' | 'allocate_interest',
    raw: string,
  ) => {
    setLineInputs((prev) => {
      const cur = prev[paydataId] ?? { paydata_p_id: paydataId, allocate_quantity: '', allocate_value: '', allocate_interest: '' }
      const value: number | '' = raw === '' ? '' : Number(raw)
      const next = { ...prev, [paydataId]: { ...cur, [field]: value } }
      scheduleSave(next, note, advancedDate)
      return next
    })
  }

  // Aggregates real-time từ local inputs.
  const totals = useMemo(() => {
    let qty = 0, val = 0, interest = 0
    for (const inp of Object.values(lineInputs)) {
      qty += typeof inp.allocate_quantity === 'number' ? inp.allocate_quantity : 0
      val += typeof inp.allocate_value === 'number' ? inp.allocate_value : 0
      interest += typeof inp.allocate_interest === 'number' ? inp.allocate_interest : 0
    }
    return { qty, val, interest }
  }, [lineInputs])

  // Sum các cột server-side cho dòng Total ở 2 bảng (Input + Finally).
  const lineSums = useMemo(() => {
    const ls = session?.lines ?? []
    return {
      to_be_allocate: ls.reduce((s, l) => s + (l.to_be_allocate ?? 0), 0),
      to_be_allocate_quant: ls.reduce((s, l) => s + (l.to_be_allocate_quant ?? 0), 0),
      interest_money: ls.reduce((s, l) => s + (l.interest_money ?? 0), 0),
      pay_paid_vnd: ls.reduce((s, l) => s + (l.pay_paid_vnd ?? 0), 0),
      allocation_money: ls.reduce((s, l) => s + (l.allocation_money ?? 0), 0),
      allocation_quantity: ls.reduce((s, l) => s + (l.allocation_quantity ?? 0), 0),
      allocation_interest: ls.reduce((s, l) => s + (l.allocation_interest ?? 0), 0),
      pay_advanced_kg: ls.reduce((s, l) => s + (l.pay_advanced_kg ?? 0), 0),
      pay_requested_vnd: ls.reduce((s, l) => s + (l.pay_requested_vnd ?? 0), 0),
      pay_advanced: ls.reduce((s, l) => s + (l.pay_advanced ?? 0), 0),
    }
  }, [session])

  // Access btnSave validations (lines 2363-2378).
  const validateBeforeSave = (): string | null => {
    if (!selectedFix) return 'Please choose Fixation No!'
    if (!advancedDate) return 'Please choose Allocated Date!'
    const linesArr = Object.values(lineInputs)
    const tobeRows = (session?.lines ?? []).filter((l) => (l.to_be_allocate ?? 0) !== 0)
    if (tobeRows.length === 0) return 'No Advanced Value to Allocate!'
    const validValueLines = linesArr.filter((l) => typeof l.allocate_value === 'number' && l.allocate_value !== 0)
    if (validValueLines.length === 0) return 'Please input Valid Allocate Value!'

    const sumTobeAmt = (session?.lines ?? []).reduce((s, l) => s + (l.to_be_allocate ?? 0), 0)
    if (totals.val > sumTobeAmt + 1e-6) return 'To be Allocate (Amount) greater than or equal to Allocate Value (Amount)!'
    const sumTobeQty = (session?.lines ?? []).reduce((s, l) => s + (l.to_be_allocate_quant ?? 0), 0)
    if (totals.qty > sumTobeQty + 1e-6) return 'To be Allocate (Quantity) greater than or equal to Allocate Value (Quantity)!'
    const sumInterest = (session?.lines ?? []).reduce((s, l) => s + (l.interest_money ?? 0), 0)
    if (totals.interest > sumInterest + 1e-6) return 'To be Allocate (Interest) greater than or equal to Allocate Value (Interest)!'
    return null
  }

  const handleSave = async () => {
    const err = validateBeforeSave()
    if (err) { snack.warning(err); return }
    if (!sessionUid) return
    // Đảm bảo PUT lần cuối trước commit (cancel debounce).
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current)
    const lines: NPEAllocationLineIn[] = Object.values(lineInputs).map((l) => ({
      paydata_p_id: l.paydata_p_id,
      allocate_quantity: l.allocate_quantity === '' ? null : Number(l.allocate_quantity),
      allocate_value: l.allocate_value === '' ? null : Number(l.allocate_value),
      allocate_interest: l.allocate_interest === '' ? null : Number(l.allocate_interest),
    }))
    try {
      await updateLines({ sessionUid, lines, note, advancedDate }).unwrap()
      const body: NPEAllocationCommitIn = { allocation_note: note, allocation_date: advancedDate, lines }
      await commitSession({ sessionUid, body }).unwrap()
      snack.success('Successfully Added')
      onCommitted()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, 'Save failed'))
    }
  }

  const handleCancel = async () => {
    if (sessionUid) {
      try { await cancelSession(sessionUid).unwrap() } catch { /* silent */ }
    }
    onClose()
  }

  const handleRefresh = () => {
    if (sessionUid) void refetch()
  }

  // Read-only display helper — TextField w/ shrunk label + roSx (canonical
  // pattern in CSFixationFormDialog). Each Access pair (label W=2268 + field
  // W=5676 twips) maps to one sm=6 TextField with its own label above.
  const RoField = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={value == null || value === '' ? '' : String(value)}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={roSx}
    />
  )
  const RoNumField = ({ label, value, digits = 0 }: { label: string; value: unknown; digits?: number }) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={fmtNum(value, digits)}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={{ ...roSx, ...numSx }}
    />
  )
  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xl" fullWidth>
      {/* Access FormHeader (Top=72): btnSave + btnCancel placed top-right.
          DialogTitle/buttons đồng nhất Sales Contract canonical (dialogStyle.ts). */}
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title="NPEs - Allocation" sx={dialogTitleTextSx}>
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>NPEs - </Box>Allocation
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={dialogTitleActionsSx}>
          {(creating || fetchingState) && <CircularProgress size={14} />}
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!sessionUid || committing || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {committing ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleCancel()}
            disabled={committing || cancelling}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* ─── Group: Allocated Information (Access Top=108, first group) ───
            Row order per Access Top coordinates (396 → 2568):
              R1: id (hidden) | advanced_date (Allocated Date)
              R2: fixation_no (combo) | fixation_date (DLookup)
              R3: contract_no (NPE Contracts) | p_contract_no (P Contracts)
              R4: price (Fixed Price) | quantity_kg (Fixed Quantity)
              R5: amount_after_fixed (Fixed Amount) | supplier (DLookup by contract_no)
              R6: allocated_quantity (label="Allocated Amount" — Access caption bug,
                  binds to quantity field) | to_be_allocate_quant (To be Allocate (Quantity))
              R7: allocated_amount (Allocated Amount) | to_be_allocate (To be Allocate (Amount))
              R8: note (Allocation Note) | must_allocate (hidden, =Round(to_be_allocate*0.7/100)*100) */}
        <Typography variant="caption" color="text.secondary" sx={{ ...sectionHeaderSx, display: 'block', mt: 0.5 }}>
          Allocated Information
        </Typography>
        <Divider sx={{ mt: 0.5, mb: 1 }} />
        <Grid container spacing={1.5}>
          {/* R1: Fixation No (primary lookup, span col 1+2) | Fixation Date (DLookup).
              Cho phép đổi Fixation No sau khi đã chọn: cancel session hiện tại
              trước khi reset state để effect tạo session mới cho lựa chọn mới. */}
          <Grid item xs={12} sm={8}>
            <Autocomplete
              size="small"
              options={fixationOptions}
              value={selectedFix}
              onChange={(_e, v) => {
                if (sessionUid) {
                  void cancelSession(sessionUid).unwrap().catch(() => { /* silent */ })
                }
                setSelectedFix(v)
                setSessionUid('')
                setLineInputs({})
                setNote('')
              }}
              getOptionLabel={(opt) => opt ? `${opt.fixation_no} — ${opt.name ?? ''}` : ''}
              isOptionEqualToValue={(o, v) => o.cs_fixation_id === v.cs_fixation_id}
              disabled={creating || cancelling}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fixation No *"
                  placeholder="Choose…"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              renderOption={(props, option) => {
                const { key: _k, ...rest } = props as { key?: Key } & HTMLAttributes<HTMLLIElement>
                return (
                  <li {...rest} key={option.cs_fixation_id}>
                    {`${option.fixation_no} — ${option.name ?? ''}`}
                  </li>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RoField label="Fixation Date" value={fmtDate(session?.fixation_date)} />
          </Grid>

          {/* R2: Allocated ID (auto) | Allocated Date (locked today) | Supplier. */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Allocated ID"
              size="small"
              fullWidth
              value=""
              placeholder="Auto-generated on save"
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <DatePicker
              label="Allocated Date *"
              format="DD-MMM-YYYY"
              disabled
              value={advancedDate ? dayjs(advancedDate) : null}
              slotProps={{ textField: { size: 'small', fullWidth: true, sx: roSx } }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RoField label="Supplier" value={session?.supplier_name ?? ''} />
          </Grid>

          {/* R3: NPE Contracts | P Contracts | Fixed Price. */}
          <Grid item xs={12} sm={4}>
            <RoField label="NPE Contracts" value={session?.contract_no ?? ''} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RoField label="P Contracts" value={session?.p_contract_no ?? ''} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RoNumField label="Fixed Price" value={session?.fixation_price} digits={2} />
          </Grid>

          {/* R4: Fixed Quantity | Fixed Amount | Allocated Amount (qty). */}
          <Grid item xs={12} sm={4}>
            <RoNumField label="Fixed Quantity" value={session?.fixation_quantity} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RoNumField label="Fixed Amount" value={session?.amount_after_fixed} />
          </Grid>
          {/* Access caption bug Label2237: "Allocated Amount" thực ra bind quantity. */}
          <Grid item xs={12} sm={4}>
            <RoNumField label="Allocated Amount" value={session?.allocated_quantity} />
          </Grid>

          {/* R5: To be Allocate Qty | Allocated Amount (value) | To be Allocate Amount. */}
          <Grid item xs={12} sm={4}>
            <RoNumField label="To be Allocate (Quantity)" value={session?.quantity_to_allocate} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RoNumField label="Allocated Amount" value={session?.allocated_amount} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <RoNumField label="To be Allocate (Amount)" value={session?.to_be_allocate} />
          </Grid>

          {/* R8: Allocation Note — full width; must_allocate hidden per Access. */}
          <Grid item xs={12}>
            <TextField
              label="Allocation Note"
              size="small"
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              value={note}
              onChange={(e) => {
                const v = e.target.value
                setNote(v)
                scheduleSave(lineInputs, v, advancedDate)
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* ─── Group: NPEs Fixation (Input) — Access Label1683, Top=3144 ───
            SubForm Alc (Access Form.frmPurchaseQryNPEAllocation_Alc).
            RecordSource = public_tmp_npe_allocation_alc; DefaultView = Datasheet.
            ColumnOrder per Access (21 cols, editable cells = allocate_quantity +
            allocate_value only — allocate_interest is auto-distributed by BE in
            choose_fixation_no, not exposed as editable column). */}
        <Typography variant="caption" color="text.secondary" sx={{ ...sectionHeaderSx, display: 'block', mt: 1.5 }}>
          NPEs Fixation (Input)
        </Typography>
        <Divider sx={{ mt: 0.5, mb: 1 }} />
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, ...compactTableSx }}>
          {(session?.lines ?? []).length === 0 ? (
            <EmptyAllocateState hasFixation={!!selectedFix} loading={fetchingState || creating} />
          ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={stickyFirstHeadSx}>Advanced ID</TableCell>
                <TableCell sx={editHeadSx} align="right">⚡ Allocate Quantity</TableCell>
                <TableCell sx={editHeadSx} align="right">⚡ Allocate Value</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Advanced No</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Amount to Allocate (Incl. Interest)</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Quantity to Allocate</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Interest to Allocate</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Advanced Date</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Fixation Date</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Interested Date (Num)</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Interest Rate (per month)</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>NPE Contract</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>P Contract</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Actual Payment VND</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Allocated Value</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Allocated Quantity</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Allocated Interest</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Advanced KG</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Advanced Price</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Requested VND</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Advanced</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* BE seed có thể trả nhiều row cùng paydata_p_id (split allocation
                  giữa nhiều fixation/annex) → key phải kèm idx để không collide. */}
              {(session?.lines ?? []).map((ln, idx) => {
                const inp = lineInputs[ln.paydata_p_id]
                return (
                  <TableRow key={`alc-${ln.paydata_p_id}-${idx}`} hover>
                    <TableCell sx={stickyFirstColSx}>{ln.paydata_p_id}</TableCell>
                    <TableCell align="right" sx={editCellSx}>
                      <AllocateNumInput
                        value={inp?.allocate_quantity ?? ''}
                        onChange={(raw) => handleLineChange(ln.paydata_p_id, 'allocate_quantity', raw)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={editCellSx}>
                      <AllocateNumInput
                        value={inp?.allocate_value ?? ''}
                        onChange={(raw) => handleLineChange(ln.paydata_p_id, 'allocate_value', raw)}
                      />
                    </TableCell>
                    <TableCell>{ln.advanced_no ?? '-'}</TableCell>
                    <TableCell align="right">{fmtNum(ln.to_be_allocate, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.to_be_allocate_quant, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.interest_money, 0)}</TableCell>
                    <TableCell>{fmtDate(ln.pay_advanced_date)}</TableCell>
                    <TableCell>{fmtDate(ln.fixation_date)}</TableCell>
                    <TableCell align="right">{ln.pay_interest_date ?? '-'}</TableCell>
                    <TableCell align="right">
                      {ln.pay_interest_rate == null ? '-' : `${(Number(ln.pay_interest_rate) * 100).toFixed(2)}%`}
                    </TableCell>
                    <TableCell>{ln.npe_contract_no ?? '-'}</TableCell>
                    <TableCell>{ln.p_contract_no ?? '-'}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_paid_vnd, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.allocation_money, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.allocation_quantity, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.allocation_interest, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_advanced_kg, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_advanced_price, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_requested_vnd, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_advanced, 0)}</TableCell>
                  </TableRow>
                )
              })}
              <TableRow>
                <TableCell sx={totalStickyFirstSx}>Total</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(totals.qty, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(totals.val, 0)}</TableCell>
                <TableCell sx={totalCellSx} />
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.to_be_allocate, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.to_be_allocate_quant, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.interest_money, 0)}</TableCell>
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.pay_paid_vnd, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.allocation_money, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.allocation_quantity, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.allocation_interest, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.pay_advanced_kg, 0)}</TableCell>
                <TableCell sx={totalCellSx} />
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.pay_requested_vnd, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.pay_advanced, 0)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          )}
        </Box>

        {/* ─── Group: NPEs Fixation (Finally) — Access Label2150, Top=5820 ───
            btnRefresh ở cùng row với group header (Access Left=2460, Top=5820).
            Sub-form AlcFinal sit directly below (Access Top=6168). */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={sectionHeaderSx}>
            NPEs Fixation (Finally)
          </Typography>
          <Tooltip title="Refresh preview from server">
            <span>
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={!sessionUid || fetchingState}
                sx={{ p: 0.25 }}
              >
                {fetchingState
                  ? <CircularProgress size={14} />
                  : <RefreshIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Divider sx={{ mt: 0.5, mb: 1 }} />
        {/* AlcFinal sub-form (Access Form.frmPurchaseQryNPEAllocation_AlcFinal).
            RecordSource = public_tmp_npe_allocation_alc (same temp table as Alc).
            Access shows ALL rows — read-only preview; btnRefresh requeries.
            Columns in Access ColumnOrder (18 cols). Note: control named
            interest_money is bound to allocate_interest (Final). */}
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, ...compactTableSx }}>
          {(session?.lines ?? []).length === 0 ? (
            <EmptyAllocateState hasFixation={!!selectedFix} loading={fetchingState || creating} />
          ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={stickyFirstHeadSx}>Advanced ID</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Advanced No</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Allocate Value (Final)</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Allocate Quantity (Final)</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Allocate Interest (Final)</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Allocated Value</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Allocated Interest</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Advanced Date</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Fixation Date</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Interested Date (Num)</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Interest Rate</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>NPE Contract</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>P Contract</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Advanced</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Requested VND</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Actual Payment VND</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Advanced KG</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">Advanced Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(session?.lines ?? []).map((ln, idx) => {
                const inp = lineInputs[ln.paydata_p_id]
                return (
                  <TableRow key={`final-${ln.paydata_p_id}-${idx}`} hover>
                    <TableCell sx={stickyFirstColSx}>{ln.paydata_p_id}</TableCell>
                    <TableCell>{ln.advanced_no ?? '-'}</TableCell>
                    <TableCell align="right">{fmtNum(inp?.allocate_value, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(inp?.allocate_quantity, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(inp?.allocate_interest, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.allocation_money, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.allocation_interest, 0)}</TableCell>
                    <TableCell>{fmtDate(ln.pay_advanced_date)}</TableCell>
                    <TableCell>{fmtDate(ln.fixation_date)}</TableCell>
                    <TableCell align="right">{ln.pay_interest_date ?? '-'}</TableCell>
                    <TableCell align="right">
                      {ln.pay_interest_rate == null ? '-' : `${(Number(ln.pay_interest_rate) * 100).toFixed(2)}%`}
                    </TableCell>
                    <TableCell>{ln.npe_contract_no ?? '-'}</TableCell>
                    <TableCell>{ln.p_contract_no ?? '-'}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_advanced, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_requested_vnd, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_paid_vnd, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_advanced_kg, 0)}</TableCell>
                    <TableCell align="right">{fmtNum(ln.pay_advanced_price, 0)}</TableCell>
                  </TableRow>
                )
              })}
              <TableRow>
                <TableCell sx={totalStickyFirstSx}>Total</TableCell>
                <TableCell sx={totalCellSx} />
                <TableCell align="right" sx={totalCellSx}>{fmtNum(totals.val, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(totals.qty, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(totals.interest, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.allocation_money, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.allocation_interest, 0)}</TableCell>
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.pay_advanced, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.pay_requested_vnd, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.pay_paid_vnd, 0)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtNum(lineSums.pay_advanced_kg, 0)}</TableCell>
                <TableCell sx={totalCellSx} />
              </TableRow>
            </TableBody>
          </Table>
          )}
        </Box>
      </DialogContent>
      {/* Access btnSave/btnCancel ở FormHeader (top) — đã render trong DialogTitle. */}
    </Dialog>
  )
}

export default NPEAllocationAddDialog
